import React, { useEffect, useState } from "react";
import { View, Text, ActivityIndicator, StyleSheet } from "react-native";
import { useNavigate } from "react-router-dom";

import global from "../../../../../styles/globalStyles";
import { Header, FooterLinks } from "../../../../common/native";
import {
  exchangeDrChronoTokenTopAesthetics,
  exchangeDrChronoTokenCameoSurgery,
} from "../../../../../services/api-integrations-drchrono";
import { logUIError } from "../../../../../services/api-misc";
import { useDrChronoConnection } from "./drchronoConnection";

type ClinicType = "top-aesthetics" | "cameo-surgery";

interface DrChronoOAuthCallbackProps {
  clinic: ClinicType;
}

console.log("🚀 DrChronoOAuthCallback component file loaded");

const DrChronoOAuthCallback: React.FC<DrChronoOAuthCallbackProps> = ({
  clinic,
}) => {
  console.log(
    "🎬 DrChronoOAuthCallback component rendering with clinic:",
    clinic,
  );
  const [status, setStatus] = useState<"loading" | "success" | "error">(
    "loading",
  );
  const [errorMessage, setErrorMessage] = useState("");
  const [hasProcessed, setHasProcessed] = useState(false);
  const navigate = useNavigate();
  const searchParams = new URLSearchParams(window.location.search);

  console.log("🔍 URL search params:", window.location.search);
  console.log("🔍 Full URL:", window.location.href);

  const { syncWithToken } = useDrChronoConnection();

  // Get the appropriate exchange function based on clinic
  const getExchangeFunction = (clinicType: ClinicType) => {
    console.log("🔧 Getting exchange function for:", clinicType);
    switch (clinicType) {
      case "top-aesthetics":
        return exchangeDrChronoTokenTopAesthetics;
      case "cameo-surgery":
        return exchangeDrChronoTokenCameoSurgery;
      default:
        throw new Error(`Unknown clinic type: ${clinicType}`);
    }
  };

  // Get clinic display name
  const getClinicDisplayName = (clinicType: ClinicType) => {
    switch (clinicType) {
      case "top-aesthetics":
        return "Top Aesthetics";
      case "cameo-surgery":
        return "Cameo Surgery";
      default:
        return "Unknown Clinic";
    }
  };

  useEffect(() => {
    console.log("⚡ useEffect triggered for clinic:", clinic);

    // Prevent multiple executions
    if (hasProcessed) {
      console.log("⏸️ Already processed, skipping...");
      return;
    }

    const code = searchParams.get("code");
    const error = searchParams.get("error");

    console.log("📝 Extracted code:", code);
    console.log("❌ Extracted error:", error);

    if (error) {
      console.log("🚫 OAuth error detected:", error);
      setStatus("error");
      setErrorMessage(
        error === "access_denied"
          ? "Authorization was cancelled."
          : `Error: ${error}`,
      );
      return;
    }

    if (!code) {
      console.log("🚫 No authorization code found");
      setStatus("error");
      setErrorMessage("Missing authorization code.");
      return;
    }

    console.log("✅ Starting token exchange process...");

    const exchangeCode = async () => {
      try {
        // Set guard immediately to prevent re-execution
        setHasProcessed(true);
        console.log("🔒 Processing guard set");

        console.log("🔄 Getting exchange function...");
        const exchangeFunction = getExchangeFunction(clinic);

        console.log("🔄 Calling exchange function with code:", code);
        const tokenResponse = await exchangeFunction(code);

        console.log("🎯 TOKEN DATA:", tokenResponse);
        console.log("🎯 ACCESS TOKEN:", tokenResponse.access_token);

        console.log("🔄 Starting sync with token...");
        await syncWithToken(tokenResponse.access_token);

        console.log("✅ Sync completed successfully");
        setStatus("success");

        console.log("🏠 Navigating to home in 1.5 seconds...");
        setTimeout(() => {
          navigate("/home");
        }, 1500);
      } catch (err: any) {
        console.error("💥 Error in exchangeCode:", err);
        console.error("💥 Error message:", err.message);
        console.error("💥 Full error:", err);
        logUIError({
          message: "DrChrono OAuth callback failed",
          page_url:
            typeof window !== "undefined" ? window.location.href : undefined,
          context: {
            component: "drchronoOAuth",
            clinic,
            hasCode: Boolean(code),
            oauthErrorParam: error,
            error: err instanceof Error ? err.message : String(err),
          },
        }).catch((logErr) =>
          console.warn("logUIError failed in drchronoOAuth", logErr),
        );

        // Reset guard on error to allow retry
        setHasProcessed(false);
        console.log("🔓 Processing guard reset due to error");

        setStatus("error");
        setErrorMessage(err.message);
      }
    };

    exchangeCode();
  }, [clinic]); // Removed syncWithToken, navigate, searchParams from dependencies

  const clinicName = getClinicDisplayName(clinic);

  console.log("🎨 Rendering with status:", status, "clinic:", clinicName);

  return (
    <View style={styles.container}>
      <Header />
      <View style={{ marginTop: 60, alignItems: "center" }}>
        {status === "loading" && (
          <>
            <Text style={global.title}>
              Authorizing {clinicName} with Dr. Chrono...
            </Text>
            <ActivityIndicator size="large" />
          </>
        )}
        {status === "success" && (
          <Text style={[global.title, { color: "green" }]}>
            {clinicName} authorization successful!
          </Text>
        )}
        {status === "error" && (
          <>
            <Text style={[global.title, { color: "red" }]}>
              {clinicName} Authorization Failed
            </Text>
            <Text style={global.errorText}>{errorMessage}</Text>
          </>
        )}
      </View>
      <FooterLinks />
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    padding: 16,
    alignItems: "center",
    justifyContent: "center",
  },
});

console.log("📤 Exporting DrChronoOAuthCallback component");

export default DrChronoOAuthCallback;

// Clinics OAuth Callbacks
export const DrChronoTopAestheticsOAuthCallback = () => (
  <DrChronoOAuthCallback clinic="top-aesthetics" />
);

export const DrChronoCameoSurgeryOAuthCallback = () => (
  <DrChronoOAuthCallback clinic="cameo-surgery" />
);
